import React from "react";
import {
  Typography,
  Box,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Collapse,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { Gauge, gaugeClasses } from "@mui/x-charts/Gauge";
import { useTheme as useMuiTheme } from "@mui/material/styles";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ThumbUpAltOutlinedIcon from "@mui/icons-material/ThumbUpAltOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { CircularProgressbar, buildStyles } from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";

const OtherScoresCard = ({ theme, simulation, focus }) => {
  const muiTheme = useMuiTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [openMap, setOpenMap] = React.useState({});
  const setOpen = (metricKey, section, value) =>
    setOpenMap((prev) => ({
      ...prev,
      [metricKey]: { ...(prev[metricKey] || {}), [section]: value },
    }));
  const toggle = (metricKey, section) =>
    setOpen(metricKey, section, !(openMap[metricKey] || {})[section]);

  function setLabel(metricKey) {
    return metricKey
      .replace(/_/g, " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  }

  const SectionHeader = ({ title, sectionKey, metricKey }) => {
    const expanded = (openMap[metricKey] || {})[sectionKey] || false;

    if (!isMobile) {
      return (
        <Typography
          sx={{
            fontSize: {
              xs: theme.typography.textMD.fontSize,
              sm: theme.typography.textLG.fontSize,
              md: theme.typography.textXL.fontSize,
            },
            fontWeight: theme.typography.fontWeightMedium,
            mb: { xs: 1, md: 0 },
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
      );
    }

    return (
      <Box
        role="button"
        aria-expanded={expanded}
        aria-controls={`${metricKey}-${sectionKey}-panel`}
        onClick={() => toggle(metricKey, sectionKey)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          py: 1,
          cursor: "pointer",
          userSelect: "none",
        }}
      >
        <Typography
          sx={{
            fontSize: {
              xs: theme.typography.textMD.fontSize,
              sm: theme.typography.textLG.fontSize,
              md: theme.typography.textXL.fontSize,
            },
            fontWeight: theme.typography.fontWeightMedium,
            color: theme.palette.text.primary,
          }}
        >
          {title}
        </Typography>
        <IconButton
          size="small"
          aria-label={expanded ? "Collapse section" : "Expand section"}
          onClick={(e) => {
            e.stopPropagation();
            toggle(metricKey, sectionKey);
          }}
          sx={{
            ml: 1,
            p: 0.5,
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
            color: theme.palette.text.primary,
          }}
        >
          <KeyboardArrowDownRoundedIcon sx={{ fontSize: 28 }} />
        </IconButton>
      </Box>
    );
  };

  const SectionBody = ({ children, metricKey, sectionKey }) => {
    const expanded = (openMap[metricKey] || {})[sectionKey] || false;

    if (!isMobile) {
      return <Box id={`${metricKey}-${sectionKey}-panel`}>{children}</Box>;
    }

    return (
      <Collapse
        in={expanded}
        unmountOnExit
        id={`${metricKey}-${sectionKey}-panel`}
      >
        {children}
      </Collapse>
    );
  };


  return (
    <Box
      sx={{
        position: "relative",
        width: { xs: "100%", md: "fit-content" },
        p: {
          xs: theme?.customSpacing?.lg ?? 2,
          md: theme.customSpacing["2xl"],
        },
        border: `2px solid ${theme.palette.border.tertiary}`,
        borderRadius: "8px",
        backgroundColor: "transparent",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        flex: 1,
        height: { xs: "auto", md: "77.5vh" },
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          width: "100%",
          pb: theme.customSpacing["3xl"],
        }}
      >
        <Typography
          sx={{
            fontWeight: theme.typography.fontWeightMedium,
            fontSize: theme.typography.textLG.fontSize,
          }}
        >
          Your score in more detail
        </Typography>
        <Typography
          sx={{
            fontStyle: "italic",
            fontWeight: theme.typography.fontWeightLight,
            fontSize: theme.typography.textSM.fontSize,
          }}
        >
          Simulation Challenge: {simulation.scenario.name || "Simulation"}
        </Typography>
      </Box>

      <Divider
        sx={{
          backgroundColor: theme.palette.border.tertiary,
          height: 2,
          alignSelf: "stretch",
          mx: `-${theme.customSpacing["2xl"]}`,
        }}
      />
      <Box sx={{ overflowY: { xs: "visible", md: "auto" }, width: "100%" }}>
        {Object.entries(simulation.evaluation).map(
          ([metricKey, metricData]) => {
            console.log(metricKey, focus);
            if (metricKey === "total_score" || metricKey === "engine_name") {
              return null;
            }

            if (focus && focus !== metricKey) return null

            const hasPos = (metricData.positives?.length ?? 0) > 0;
            const hasRec = (metricData.recommendations?.length ?? 0) > 0;

            const shouldDisplay = isMobile && metricKey !== focus ? "none" : "flex";

            return (
              <Box
                key={metricKey}
                sx={{
                  display: shouldDisplay, // Fixed: Use shouldDisplay directly
                  flexDirection: "column",
                  p: theme.customSpacing["3xl"],
                }}
              >
                {/* Score header */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "100%",
                    mb: theme.customSpacing["4xl"],
                  }}
                >
                  <Box sx={{ width: 200, height: "110px" }}>
                    <CircularProgressbar
                      value={metricData.value}
                      text={(metricData.value / 10).toFixed(1)}
                      strokeWidth={12.5}
                      circleRatio={0.5}
                      styles={buildStyles({
                        rotation: 0.75,
                        strokeLinecap: "round",
                        pathColor: theme.palette.border.brand,
                        trailColor: theme.palette.border.secondary,
                        textColor: theme.palette.text.primary,
                        pathTransition: "stroke-dashoffset 0.5s ease 0s",
                      })}
                    />
                  </Box>
                  <Typography
                    sx={{
                      mt: theme.customSpacing["xl"],
                      fontSize: theme.typography.displayXS.fontSize,
                      fontWeight: theme.typography.fontWeightMedium,
                    }}
                  >
                    {metricData?.label || setLabel(metricKey)}
                  </Typography>
                </Box>

                {/* Sections container */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    width: "100%",
                    gap: { xs: 2, md: 0 },
                    "@media (max-width:1080px)": {
                      flexDirection: "column",
                    },
                  }}
                >
                  {hasPos && (
                    <Box sx={{ flex: 1, minWidth: 0, pr: { md: 2 } }}>
                      <SectionHeader
                        title="Here's what went well 👏"
                        sectionKey="positives"
                        metricKey={metricKey}
                      />
                      <SectionBody metricKey={metricKey} sectionKey="positives">
                        <List dense sx={{ width: "100%" }}>
                          {metricData.positives.map((item, index) => (
                            <ListItem
                              key={`${metricKey}-positive-${index}`}
                              alignItems="flex-start"
                              sx={{
                                p: 0,
                                minHeight: "48px",
                                alignItems: "flex-start",
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  variant="square"
                                  sx={{
                                    backgroundColor:
                                      theme.palette.background.button_primary,
                                    color: theme.palette.background.primary,
                                    borderRadius: 1,
                                    height: "24px",
                                    width: "24px",
                                  }}
                                >
                                  <ThumbUpAltOutlinedIcon
                                    sx={{
                                      fontSize:
                                        theme.typography.textMD.fontSize,
                                    }}
                                  />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </SectionBody>
                    </Box>
                  )}

                  {hasPos && hasRec && (
                    <>
                      <Divider
                        sx={{
                          display: { xs: "block", md: "none" },
                          my: 1,
                          height: "2px",
                          backgroundColor: theme.palette.border.tertiary,
                          width: "100%",
                        }}
                      />
                      <Divider
                        orientation="vertical"
                        flexItem
                        sx={{
                          display: { xs: "none", md: "block" },
                          backgroundColor: theme.palette.border.tertiary,
                          mx: 4,
                          width: "2px",
                        }}
                      />
                    </>
                  )}

                  {hasRec && (
                    <Box sx={{ flex: 1, minWidth: 0, pl: { md: 2 } }}>
                      <SectionHeader
                        title="Try these recommendations 💪"
                        sectionKey="recommendations"
                        metricKey={metricKey}
                      />
                      <SectionBody
                        metricKey={metricKey}
                        sectionKey="recommendations"
                      >
                        <List dense sx={{ width: "100%" }}>
                          {metricData.recommendations.map((item, index) => (
                            <ListItem
                              key={`${metricKey}-recommendation-${index}`}
                              alignItems="flex-start"
                              sx={{
                                p: 0,
                                minHeight: "48px",
                                alignItems: "flex-start",
                              }}
                            >
                              <ListItemAvatar>
                                <Avatar
                                  variant="square"
                                  sx={{
                                    backgroundColor:
                                      theme.palette.background.button_primary,
                                    color: theme.palette.background.primary,
                                    borderRadius: 1,
                                    height: "24px",
                                    width: "24px",
                                  }}
                                >
                                  <TrendingUpIcon
                                    sx={{
                                      fontSize:
                                        theme.typography.textMD.fontSize,
                                    }}
                                  />
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText primary={item} />
                            </ListItem>
                          ))}
                        </List>
                      </SectionBody>
                    </Box>
                  )}
                </Box>
              </Box>
            );
          }
        )}
      </Box>
    </Box>
  );
};

export default OtherScoresCard;